import Phaser from 'phaser';
import type { DeformClipSet } from '@gooddesign/anim-schema';
import type { ClipName } from '../anim/clips';
import { MeshPlayer } from './MeshPlayer';

// Rig — an Actor's deforming visual body. It wraps the MeshPlayer (the baked
// clip-set playback) and presents the Actor a small surface: play a clip, place
// it at the feet, face a direction. The Rig is the "recreate Character Animator's
// warp" renderer end; today it plays a placeholder bake, tomorrow the real
// Marionette export — the Rig API doesn't change when the source does, because
// both are the same DeformClipSet.

export interface RigOptions {
  scale?: number;
  flipV?: boolean;
  depth?: number;
  /** Build the mirrored ground reflection (driven by Rig.reflect()). */
  reflection?: boolean;
  /** Build the ground cast-shadow (driven by Rig.castShadow()). */
  shadow?: boolean;
  /** Opt into normal-mapped scene lighting (Order 16.2). Every part texture must
   *  carry a normal-map data source; self-shadow adds contact-shadow depth. */
  lit?: boolean;
  selfShadow?: boolean;
}

export class Rig {
  private player: MeshPlayer;

  constructor(scene: Phaser.Scene, clips: DeformClipSet, opts: RigOptions = {}) {
    // Mesh2D samples textures bottom-up; our baked UVs put v=0 at each part's
    // top, so flip V by default to render the figure right-way-up.
    this.player = new MeshPlayer(scene, clips, {
      scale: opts.scale ?? 1,
      flipV: opts.flipV ?? true,
      reflection: opts.reflection ?? false,
      shadow: opts.shadow ?? false,
    });
    if (opts.depth !== undefined) this.player.setDepth(opts.depth);
    if (opts.lit) this.player.setLighting(true, opts.selfShadow ?? true);
  }

  play(clip: ClipName): void {
    this.player.play(clip);
  }

  get playing(): ClipName | null {
    return this.player.playing;
  }

  /** True once the current one-shot clip (jump/attack/hit) has finished. */
  isDone(): boolean {
    return this.player.isDone();
  }

  /** Place the rig so its feet-origin sits at (x, y) in world px. */
  place(x: number, y: number): void {
    this.player.setPosition(x, y);
  }

  face(dir: 1 | -1): void {
    this.player.setFacing(dir);
  }

  /** Render scale, driven per frame from the actor's depth on the belt. */
  scale(s: number): void {
    this.player.setScale(s);
  }

  /** Draw order. `body` and `reflection` come from ground.ts and live in bands
   *  that cannot interleave. */
  depth(body: number, reflection: number): void {
    this.player.setDepth(body);
    this.player.setReflectionDepth(reflection);
  }

  /** How wet the ground under this rig is (0 = dry), and the world y of the
   *  water's surface it mirrors about. */
  reflect(strength: number, planeY: number): void {
    this.player.setReflection(strength, planeY);
  }

  /** How this rig throws its cast shadow: screen rake (dx, dy) per unit of the
   *  figure's height, and opacity. Set by the Scene from the shot's sun. */
  castShadow(dx: number, dy: number, strength: number, softness = 0): void {
    this.player.setShadow(dx, dy, strength, softness);
  }

  /** How deep in the plate's shadow this rig stands (0..1) — darkens it. */
  shade(strength: number): void {
    this.player.setShade(strength);
  }

  /** Base depth for the shade overlay (same band as the body). */
  shadeDepth(d: number): void {
    this.player.setShadeDepth(d);
  }

  /** Part keys in paint order — how a runtime channel finds 'head'. */
  partKeys(): string[] {
    return this.player.partKeys();
  }

  /** Figure rest height in rig units (runtime channels' length scale). */
  get figureRigHeight(): number {
    return this.player.figureRigHeight;
  }

  /** Additive rig-space offsets over the baked frame, one per part (attention's
   *  seam). Null clears. */
  partOffsets(offsets: ReadonlyArray<{ dx: number; dy: number } | null> | null): void {
    this.player.setPartOffsets(offsets);
  }

  update(dtSec: number): void {
    this.player.update(dtSec);
  }

  destroy(): void {
    this.player.destroy();
  }
}
